import { ID, LinkOptions, TaskLinkFields } from '@griply/types';

import { taskGoalIds, taskLifeAreaIds } from './task-link-ids';

export type ResolvedTaskParent<
  GoalLike extends { id: ID },
  LifeAreaLike extends { id: ID },
> =
  | { kind: LinkOptions.Goal; goal: GoalLike }
  | { kind: LinkOptions.LifeArea; lifeArea: LifeAreaLike };

export type ResolveTaskParentsOptions<
  GoalLike extends { id: ID },
  LifeAreaLike extends { id: ID },
> = {
  goals: GoalLike[];
  lifeAreas: LifeAreaLike[];
};

// A task's parents live on its own document, so unlike resolveGoalParents this needs no links graph.
export const resolveTaskParents = <
  GoalLike extends { id: ID },
  LifeAreaLike extends { id: ID },
>(
  task: TaskLinkFields,
  { goals, lifeAreas }: ResolveTaskParentsOptions<GoalLike, LifeAreaLike>,
): ResolvedTaskParent<GoalLike, LifeAreaLike>[] => {
  const parentGoals = taskGoalIds(task).flatMap(
    (goalId): ResolvedTaskParent<GoalLike, LifeAreaLike>[] => {
      const goal = goals.find(({ id }) => id === goalId);
      return goal ? [{ kind: LinkOptions.Goal, goal }] : [];
    },
  );
  const parentLifeAreas = taskLifeAreaIds(task).flatMap(
    (lifeAreaId): ResolvedTaskParent<GoalLike, LifeAreaLike>[] => {
      const lifeArea = lifeAreas.find(({ id }) => id === lifeAreaId);
      return lifeArea ? [{ kind: LinkOptions.LifeArea, lifeArea }] : [];
    },
  );

  return [...parentGoals, ...parentLifeAreas];
};
